import { captureSentryException, sentrySetup } from "@-/dev.improve.haniverse.lib.sentry";
import { installStuckLockReporter } from "@-/dev.improve.haniverse.lib.database.engine";
import { setupConsola } from "@-/dev.improve.haniverse.lib.logger";
import {
  PersistentKeyboardDismissButton,
  useCollapsiblePersistentKeyboard,
  usePersistentKeyboardDismissButtonGate,
} from "@-/dev.improve.haniverse.components.collapsible-persistent-keyboard";
import {
  RootContextProvider,
  type RootLayoutNavigationDevtoolsProps,
  type UseRootLayoutNavigationDevtoolsGate,
  type UseRootLayoutShellDevtools,
} from "@-/dev.improve.haniverse.routes._context";
import { Stack } from "@-/com.npmjs.expo-router";
import { useEffect, type ComponentType } from "react";
import { PremiumWelcomeSheet, SuperwallGate } from "@-/dev.improve.haniverse.components.superwall";
import { SentenceVoiceChooserSheet } from "@-/dev.improve.haniverse.components.sentence-voice";
import { Platform } from "@-/com.npmjs.react-native";
import { safeSetTimeout } from "@-/dev.improve.haniverse.lib.scheduler";
import { DevBuildFooter } from "@-/dev.improve.haniverse.components.bundle-stamp";
import {
  DiagnosticsRouteObserver,
  useDiagnosticsRecorder,
  useRecentDebugLogRecorder,
} from "@-/dev.improve.haniverse.lib.diagnostics";

type RootLayoutProps = {
  NavigationDevtools?: ComponentType<RootLayoutNavigationDevtoolsProps>;
  useNavigationDevtoolsGate?: UseRootLayoutNavigationDevtoolsGate;
  useShellDevtools?: UseRootLayoutShellDevtools;
};

const POST_BOOT_UPDATE_CHECK_DELAY_MS = 30_000;

async function cacheAvailableUpdateAfterBoot(): Promise<void> {
  const Updates = await import("@-/com.npmjs.expo-updates");
  if (!Updates.isEnabled) {
    return;
  }

  const update = await Updates.checkForUpdateAsync();
  if (update.isAvailable) {
    // Cache only. Never reload an active session automatically; the update applies next launch.
    await Updates.fetchUpdateAsync();
  }
}

/** The native entry loads this layout underneath the splash, before the first-frame handoff. */
function RootLayoutInner({
  NavigationDevtools,
  useNavigationDevtoolsGate,
  useShellDevtools,
}: RootLayoutProps) {
  useCollapsiblePersistentKeyboard();
  const keyboardDismissButtonGate = usePersistentKeyboardDismissButtonGate();
  // Opt-in diagnostics recording (Settings → Diagnostics). Bound here, above every screen, so a
  // device with the setting on records from the first render of a launch.
  useDiagnosticsRecorder();
  useRecentDebugLogRecorder();

  useEffect(() => {
    setupConsola();

    if (__DEV__ || Platform.OS === "web") {
      return;
    }

    // Expo Updates is configured with checkAutomatically=NEVER. Its optional network lookup is
    // delayed well beyond native splash removal and the first interaction so it cannot compete
    // with launch, route discovery, or first-screen data.
    const timer = safeSetTimeout(
      () => {
        void cacheAvailableUpdateAfterBoot().catch((error: unknown) => {
          captureSentryException(error, {
            level: "warning",
            tags: { "expo_updates.post_boot_check": "true" },
          });
        });
      },
      POST_BOOT_UPDATE_CHECK_DELAY_MS,
      "post-boot-update-check",
    );

    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <SuperwallGate>
      <RootContextProvider
        NavigationDevtools={NavigationDevtools}
        useNavigationDevtoolsGate={useNavigationDevtoolsGate}
        useShellDevtools={useShellDevtools}
      >
        <Stack
          screenOptions={{
            // Entry redirects only select the application shell. A native transition here can
            // outlive the destination's first commit and slide underneath the splash fade.
            // Screens own their transitions in the nested stacks.
            animation: "none",
            gestureEnabled: false,
            headerShown: false,
            keyboardHandlingEnabled: false,
          }}
        />
        {keyboardDismissButtonGate !== null && (
          <PersistentKeyboardDismissButton gate={keyboardDismissButtonGate} />
        )}
        <DiagnosticsRouteObserver />
        <PremiumWelcomeSheet />
        <SentenceVoiceChooserSheet />
      </RootContextProvider>
    </SuperwallGate>
  );
}

// Route stuck-actor-lock reports to Sentry on the MAIN THREAD.
//
// The watchdog (`report-stuck-connection-lock.ts`) deliberately holds no static Sentry import: it is
// bundled into the OPFS leader worker, and importing Sentry there drags varlock's unguarded
// `process.env` read into a Web Worker with no `process` — which threw at worker startup and hung
// every actor query (runs 33231390463 / 33237237844). Injecting the sink here keeps the reporting
// while leaving the worker bundle free of both.
// eslint-disable-next-line no-top/no-top-level-side-effects -- module-init wiring, same shape as `sentrySetup` below
installStuckLockReporter(({ contexts, error, fingerprint, tags }) => {
  captureSentryException(error, { contexts, fingerprint, level: "error", tags });
});
// eslint-disable-next-line no-top/no-top-level-side-effects -- Sentry.wrap must install the root error boundary once, outside render.
const SentryRootLayout = sentrySetup(RootLayoutInner);

export function RootLayout(props: RootLayoutProps) {
  return (
    <>
      <SentryRootLayout {...props} />
      <DevBuildFooter />
    </>
  );
}
